import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import { FileText, Loader2, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const ACCOUNTING_PROVIDER_CACHE_KEY = "obihub_accounting_provider_last";
const READ_MODEL_CONFIG = {
  silent_statuses: [404],
  no_retry: true,
  redact_telemetry: true,
  expose_error_meta: true,
};

function getCachedAccountingProvider() {
  try {
    const raw = String(sessionStorage.getItem(ACCOUNTING_PROVIDER_CACHE_KEY) || "").trim();
    return raw === "parasut" || raw === "bizimhesap" ? raw : "";
  } catch {
    return "";
  }
}

function cacheAccountingProvider(provider) {
  try {
    if (provider === "parasut" || provider === "bizimhesap") {
      sessionStorage.setItem(ACCOUNTING_PROVIDER_CACHE_KEY, provider);
    }
  } catch {
    // ignore cache errors
  }
}

function resolveActiveProvider(cfg) {
  const preferred = String(cfg?.provider || "").toLowerCase();
  const parasutConnected = Boolean(cfg?.parasut?.connected);
  const bizimhesapConnected = Boolean(cfg?.bizimhesap?.connected);
  if (preferred === "bizimhesap" && bizimhesapConnected) return "bizimhesap";
  if (preferred === "parasut" && parasutConnected) return "parasut";
  if (bizimhesapConnected) return "bizimhesap";
  if (parasutConnected) return "parasut";
  return "";
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("tr-TR");
}

function mapExpenseStatus(status) {
  const raw = String(status || "").trim().toLowerCase();
  if (!raw) return "-";
  if (raw === "unknown") return "Bilinmiyor";
  if (raw === "partial") return "Kısmi ödendi";
  if (raw === "overdue") return "Gecikmiş";
  if (raw === "pending") return "Ödeme bekliyor";
  if (raw === "unpaid" || raw === "ödenmedi") return "Ödenmedi";
  if (raw === "paid" || raw === "ödendi") return "Ödendi";
  if (raw.includes("draft") || raw.includes("taslak")) return "Taslak";
  if (raw.includes("cancel") || raw.includes("iptal")) return "İptal";
  if (raw.includes("pending") || raw.includes("bek")) return "Beklemede";
  if (raw.includes("success")) return "Başarılı";
  return status || "-";
}

function formatDecimalMoney(value, currency) {
  if (value === null || value === undefined || value === "") return "-";
  const raw = String(value).trim();
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(raw);
  if (!match) return "-";
  const [, sign, whole, fraction = ""] = match;
  const grouped = BigInt(whole).toLocaleString("tr-TR");
  const amount = `${sign}${grouped},${fraction.padEnd(2, "0")}`;
  const code = String(currency || "").toUpperCase();
  if (code === "TRY") return `${amount} ₺`;
  return code ? `${amount} ${code}` : amount;
}

function providerLabel(provider) {
  if (provider === "parasut") return "Paraşüt";
  if (provider === "bizimhesap") return "BizimHesap";
  return provider || "";
}

function expenseStatusBadge(status) {
  const label = mapExpenseStatus(status);
  const raw = String(label || "").toLowerCase();
  const isGood = raw.includes("ödendi") || raw.includes("başarılı");
  const cls = isGood
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : "bg-muted/60 text-foreground/80 border-border";
  return <span className={`inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-md text-xs leading-none border ${cls}`}>{label}</span>;
}

export default function ExpenseInvoicesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notConnected, setNotConnected] = useState(false);
  const [accountingProvider, setAccountingProvider] = useState("");
  const [providerUnsupported, setProviderUnsupported] = useState(false);
  const [dataMode, setDataMode] = useState("loading"); // loading | readmodel | legacy | error
  const [payableSummary, setPayableSummary] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const requestRef = useRef(0);
  const perPage = 20;

  const fetchData = useCallback(async () => {
    const requestId = ++requestRef.current;
    setLoading(true);
    setDataMode("loading");
    setPayableSummary([]);
    try {
      try {
        const data = await api.get(
          `/accounting/read-model/purchase-invoices?page=${page}&per_page=${perPage}`,
          READ_MODEL_CONFIG
        );
        if (requestId !== requestRef.current) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
        setTotal(Number.isInteger(data?.total) ? data.total : 0);
        setNotConnected(false);
        setProviderUnsupported(false);
        setAccountingProvider("");
        setDataMode("readmodel");
        try {
          const summary = await api.get("/accounting/read-model/payables/summary", READ_MODEL_CONFIG);
          if (requestId !== requestRef.current) return;
          setPayableSummary(Array.isArray(summary?.currencies) ? summary.currencies : []);
        } catch {
          // Fatura listesi kullanılabilir durumdaysa ikincil özet hatası ana görünümü kapatmamalı.
          if (requestId === requestRef.current) setPayableSummary([]);
        }
        return;
      } catch (readModelError) {
        if (requestId !== requestRef.current) return;
        if (readModelError?.status !== 404) {
          setItems([]);
          setTotal(0);
          setNotConnected(false);
          setProviderUnsupported(false);
          setAccountingProvider("");
          setDataMode("error");
          return;
        }
      }

      const cfg = await api.get("/settings/accounting").catch(() => null);
      if (requestId !== requestRef.current) return;
      const provider = resolveActiveProvider(cfg) || getCachedAccountingProvider();
      if (!provider) {
        setNotConnected(true);
        setProviderUnsupported(false);
        setAccountingProvider("");
        setItems([]);
        setTotal(0);
        setDataMode("legacy");
        return;
      }
      if (provider === "bizimhesap") {
        setNotConnected(false);
        setProviderUnsupported(true);
        setAccountingProvider(provider);
        setItems([]);
        setTotal(0);
        setDataMode("legacy");
        return;
      }
      setNotConnected(false);
      setProviderUnsupported(false);
      setAccountingProvider(provider);
      cacheAccountingProvider(provider);
      const base = provider === "bizimhesap" ? "/bizimhesap/expense-invoices" : "/parasut/expense-invoices";
      const data = await api.get(`${base}?page=${page}&per_page=${perPage}`);
      if (requestId !== requestRef.current) return;
      setItems(data?.invoices || []);
      setTotal(Number(data?.total || 0));
      setDataMode("legacy");
    } catch (e) {
      if (requestId !== requestRef.current) return;
      setItems([]);
      setTotal(0);
      setDataMode("error");
      toast.error(e.message || "Gider faturaları yüklenemedi");
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((x) =>
      [x.description, x.invoice_no, x.document_number, x.supplier_name, x.status, x.payment_status]
        .map((v) => String(v || "").toLowerCase())
        .some((v) => v.includes(q))
    );
  }, [items, search]);

  const payable = payableSummary.find((x) => x.currency === "TRY") || payableSummary[0] || null;

  if (notConnected) {
    return (
      <div className="page-shell min-h-[calc(100dvh-3.5rem)]">
        <div className="surface-card p-6 text-sm text-muted-foreground">
          Bu ekran için bağlı muhasebe uygulaması bulunamadı.
        </div>
      </div>
    );
  }

  if (providerUnsupported) {
    return (
      <div className="page-shell min-h-[calc(100dvh-3.5rem)]">
        <div className="surface-card p-6 text-sm text-muted-foreground">
          BizimHesap API dokümanında gider faturası listeleme servisi tanımlı değil. Bu ekran sadece Paraşüt entegrasyonunda kullanılabilir.
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell min-h-[calc(100dvh-3.5rem)]">
      <div className="surface-card overflow-hidden">
        <div className="sheet-heading flex-wrap gap-y-3">
          <div>
            <h2>{dataMode === "readmodel" ? "Alış & Gider Faturaları" : "Gider Faturaları"}</h2>
            {!loading ? <p>{total} kayıt{accountingProvider ? ` · ${providerLabel(accountingProvider)}` : ""}</p> : null}
          </div>
          <div className="sheet-heading-actions">
            <div className="flex items-center gap-3 rounded-lg border bg-background px-3 h-10 w-full max-w-sm">
              <Search className="h-4 w-4 text-muted-foreground shrink-0" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ara..." className="flex-1 bg-transparent text-sm outline-none" />
            </div>
          </div>
        </div>

        {dataMode === "readmodel" && payable ? (
          <div className="grid gap-3 border-b border-border bg-muted/20 px-4 py-3 sm:grid-cols-3" data-testid="payables-summary">
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Bekleyen borç</p><strong>{formatDecimalMoney(payable.remaining, payable.currency)}</strong></div>
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Gecikmiş</p><strong>{formatDecimalMoney(payable.overdue, payable.currency)}</strong></div>
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Açık fatura</p><strong>{payable.open_count}</strong></div>
          </div>
        ) : null}

        <div className="p-3 md:p-4">
          {dataMode === "error" ? (
            <div className="h-56 flex flex-col items-center justify-center gap-2 text-center text-muted-foreground" data-testid="read-model-error">
              <FileText className="h-6 w-6" />
              <strong className="text-foreground">Veriler şu an görüntülenemiyor</strong>
              <span className="text-sm">Kayıtların güvenli biçimde okunabilmesi için daha sonra tekrar deneyin.</span>
            </div>
          ) : loading ? (
            <div className="h-56 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor...
            </div>
          ) : filtered.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-muted-foreground">
              <FileText className="h-5 w-5 mr-2" /> Kayıt bulunamadı
            </div>
          ) : (
            <div className="overflow-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Tarih</th>
                    <th className="text-left px-3 py-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Fatura No</th>
                    <th className="text-left px-3 py-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Tedarikçi</th>
                    <th className="text-left px-3 py-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">{dataMode === "readmodel" ? "Kaynak" : "Açıklama"}</th>
                    <th className="text-left px-3 py-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Durum</th>
                    <th className="text-right px-3 py-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((x) => (
                    <tr key={x.id} className="border-b border-border/60">
                      <td className="px-3 py-2">{formatDate(x.issue_date)}</td>
                      <td className="px-3 py-2">{x.document_number || x.invoice_no || "-"}</td>
                      <td className="px-3 py-2">{x.supplier_name || x.contact_name || "-"}</td>
                      <td className="px-3 py-2">{dataMode === "readmodel" ? (x.source === "obihub" ? "ObiHub" : "Eski kayıt") : (x.description || "-")}</td>
                      <td className="px-3 py-2">{expenseStatusBadge(dataMode === "readmodel" ? x.payment_status : x.status)}</td>
                      <td className="px-3 py-2 text-right">
                        {dataMode === "readmodel" && x.total_quality === "unknown"
                          ? <span className="text-muted-foreground">Tutar alınamadı</span>
                          : dataMode === "readmodel"
                            ? formatDecimalMoney(x.total, x.currency)
                            : formatCurrency(Number(x.gross_total || 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
